import { Heading, Stack, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";

import { UserExistsResponse } from "@ud/api";
import { usePutOfferStatusChangeMutation } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";
import Container from "components/Chakra/Container";
import CustomSpinner from "components/CustomSpinner";
import { useTracking } from "contexts/Tracking";
import useApiEndpoint from "hooks/useApiEndpoint";
import useGlobalState from "hooks/useGlobalState";
import useHelperCRM, { EAction } from "hooks/useHelperCRM";
import useRouter from "hooks/useRouter";
import useUserCRM from "hooks/useUserCRM";
import { useAlertModal } from "modals/useAlertModal";
import { useInfoINContactModal } from "modals/useInfoINContactModal";
import { getTypeUser } from "modals/useLoginModal/components/UserType";
import { IMyOffer } from "utils/offer";
import MyOffersApiEndpoint from "utils/offer/api/MyOffers";
import { ETypeUser } from "utils/user";
import { EOrderTypeId } from "utils/payment/types";
import CardOffer from "./CardOffer";
import Header from "./Header";
import IdealniNajemceBanner from "./IdealniNajemceBanner";
import TopActionButton from "./TopActionButton";

const getPaymentStatus = (): Props["payment"] => {
  const url = typeof window === "undefined" ? "" : window.location.href;

  if (url.includes("/platba-probehla-uspesne")) {
    return "success";
  }

  if (url.includes("/platba-selhala")) {
    return "error";
  }

  return undefined;
};

export type TActivation = "activate" | "deactivate" | "expired";

type Props = {
  payment?: "success" | "error";
  isInOffer?: boolean;
};

const MyOffers: React.FC<Props> = ({
  payment = getPaymentStatus(),
  isInOffer = false,
}) => {
  const [offers, setOffers] = useState<Array<IMyOffer>>([]);
  const [userType, setUserType] = useState<ETypeUser>(ETypeUser.NONE);
  const [isLoading, setLoading] = useState(true);

  const { navigateTo, pathname, query } = useRouter();
  const queryOfferId = query?.offer_id?.toString() || null;
  const { trackEvent } = useTracking();
  const { user } = useGlobalState();
  const { createEventOffer, updateUserOffer } = useHelperCRM();
  const { getUserByEmail } = useUserCRM();
  const { openModal: openAlertModal } = useAlertModal();
  const { openModal: openINContactModal } = useInfoINContactModal();

  const { callEndpoint: myOffersApi } = useApiEndpoint(MyOffersApiEndpoint);

  const [changeOfferStatusApi] = usePutOfferStatusChangeMutation();

  const getOffers = useCallback(async () => {
    const resData = await myOffersApi();
    if (resData?.data) {
      setOffers(resData.data);
      setLoading(false);
    }
  }, [myOffersApi]);

  useEffect(() => {
    if (pathname.includes("in-inzerat")) {
      openINContactModal();
    }
  }, [openINContactModal, pathname]);

  useEffect(() => {
    if (isInOffer) {
      openINContactModal();
    }
  }, [isInOffer, isLoading, openINContactModal]);

  useEffect(() => {
    if (!user) {
      return;
    }
    getOffers();
    if (payment) {
      const orderType = query?.orderType;
      const isOfferPayment =
        orderType &&
        [
          EOrderTypeId.INSERT_OFFER_FREE,
          EOrderTypeId.INSERT_OFFER_STANDARD,
          EOrderTypeId.INSERT_OFFER_VIP,
        ].includes(Number(orderType));

      if (orderType && !isOfferPayment) {
        return;
      }

      if (user && payment === "success") {
        trackEvent({
          "dl.eCat": "offer_create",
          "dl.eAct": "dialog.shown",
          "dl.eLab": "Zaplacený inzerát",
        });
        createEventOffer({ action: EAction.PUBLISH });
      }

      if (queryOfferId) {
        updateUserOffer({
          offerId: queryOfferId,
          data: {
            id: queryOfferId,
            date: new Date().toISOString(),
            offerId: queryOfferId,
            status: "offer_active",
            location: "idiot",
          },
        });
      }
      openAlertModal({
        animation: payment === "success" ? "check" : "error",
        title:
          payment === "success"
            ? "Platba proběhla úspěšně"
            : "Platba se nezdařila",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, payment]);

  useEffect(() => {
    if (user) {
      const callUserCRM = async () => {
        const infoUser = await getUserByEmail(user.email);
        if (infoUser && infoUser.type) {
          const type = getTypeUser({
            typeValue: infoUser.type as UserExistsResponse["type"],
          });

          type?.apiId && setUserType(type?.apiId);
        }
      };
      callUserCRM();
    }
  }, [user, getUserByEmail]);

  const deactivateOnSubmit = async (id: number): Promise<void> => {
    createEventOffer({ action: EAction.DEACTIVATE });
    updateUserOffer({
      offerId: id.toString(),
      data: {
        id: id.toString(),
        date: new Date().toISOString(),
        offerId: id.toString(),
        status: "offer_paused",
      },
    });

    await changeOfferStatusApi({
      statusChangeRequest: { offerId: id, status: "deactivate" },
    });
    getOffers();
  };

  const activateOnSubmit = async (id: number) => {
    try {
      createEventOffer({ action: EAction.PUBLISH });
      const data = await changeOfferStatusApi({
        statusChangeRequest: { offerId: id, status: "activate" },
      });
      if ("data" in data && data.data.success === true) {
        getOffers();
        updateUserOffer({
          offerId: id.toString(),
          data: {
            id: id.toString(),
            date: new Date().toISOString(),
            offerId: id.toString(),
            status: "offer_active",
          },
        });
      } else {
        navigateTo("/cenik/[offerId]", { offerId: id.toString() });
      }
    } catch (err) {
      navigateTo("/cenik/[offerId]", { offerId: id.toString() });
    }
  };

  const onSubmitHandler = (id: number, action: TActivation) => {
    switch (action) {
      case "activate":
        activateOnSubmit(id);

        return;
      case "deactivate":
        deactivateOnSubmit(id);

        return;
      case "expired":
        navigateTo("/cenik/[offerId]", { offerId: id.toString() });
    }
  };

  const activationHandler = async (id: number, action: TActivation) => {
    openAlertModal({
      title:
        action === "deactivate"
          ? "Chtete zastavit zveřejnění inzerátu?"
          : "Opravdu chcete zveřejnit inzerát?",
      children: action === "deactivate" && (
        <Text textAlign="center">
          Stačí, aby inzerát <b>nebyl zveřejněný</b>
          <br />
          a nikdo ho neuvidí.
        </Text>
      ),
      submitText: action === "deactivate" ? "ANO, zastavit" : "ANO, zveřejnit",
      cancelText: action === "deactivate" ? "NE, ponechat" : "NE, děkuji",
      onSubmit: () => onSubmitHandler(id, action),
    });
  };

  return (
    <>
      <Container mb="90px" mt="48px" data-test="myOffers">
        <Heading as="h1" mb="24px" data-test="myOffers.heading">
          Moje inzeráty
        </Heading>

        {isLoading ? (
          <CustomSpinner
            position="absolute"
            top="30%"
            left="50%"
            transform="translate(-50%, -30%)"
          />
        ) : (
          <>
            <Header offers={offers} />
            <Stack
              data-test="myOffers.offers"
              pt="40px"
              direction="row"
              columnGap={3}
              rowGap={{ base: 3, md: 5 }}
              flexWrap="wrap"
              justify="center"
              h="100%"
            >
              {userType === ETypeUser.PRIVATE_LANDLORD &&
                offers.length >= 1 && <IdealniNajemceBanner />}
              {offers.map((offer) => (
                <Stack
                  key={offer.id}
                  spacing="4"
                  data-test="myOffers.offerCard"
                >
                  <TopActionButton
                    id={offer.id}
                    status={offer.status}
                    getOffers={getOffers}
                    activationHandler={activationHandler}
                  />
                  <CardOffer
                    paidTypeId={offer.paidTypeId}
                    activationHandler={activationHandler}
                    offer={offer}
                    status={offer.status}
                  />
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </Container>
    </>
  );
};

export default MyOffers;
